import { TagOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../store/hooks";
import ProfileDropdown from "../../../components/ProfileDropdown";
import BackButton from "../../../components/BackButton";
import { useViewport } from "../../../hooks/useViewport";
import { useOrderingLogout } from "./useOrderingLogout";
import { R } from "./orderingTheme";

interface OrderingTopBarProps {
  /** Връщане една стъпка назад В модула (продукти → маси). Ако е подаден,
      стрелката вика него вместо да излиза към модулите — така от продуктите
      първото натискане връща при масите, а второто (вече без onBack) излиза. */
  onBack?: () => void;
}

export default function OrderingTopBar({
  onBack,
}: OrderingTopBarProps) {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const navigate = useNavigate();

  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const access = useAppSelector((s) => s.auth.access);
  const roles = useAppSelector((s) => s.auth.roles);
  // Стрелката се показва при няколко роли (изход към модулите) И винаги в
  // продуктовия изглед (onBack) — там тя е единственият „назад" на телефон
  // след премахването на мобилния хедър (0.0.117)
  const showBack = roles.length > 1 || !!onBack;
  const displayName = names || username || "";
  // Изходът е общ с футъра на продуктовия изглед (useOrderingLogout)
  const { handleLogout, logoutModal } = useOrderingLogout();

  return (
    <>
      <header
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
          gap: 10,
          fontFamily: R.font,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1, lineHeight: 1 }}
        >
          {showBack && <BackButton onClick={onBack ?? (() => navigate("/"))} />}
          <img
            src="/logo-ts.png"
            alt="UnrealSoft"
            style={{
              width: isNarrow ? 26 : 36,
              height: isNarrow ? 26 : 36,
              objectFit: "contain",
              flexShrink: 0,
              userSelect: "none",
            }}
          />
          {/* На телефон „UnrealSoft" се крие — остава само логото,
              за да има място за името до профил иконата. */}
          {!isNarrow && (
            <div style={{ minWidth: 0, flex: 1, lineHeight: 1 }}>
              <h1
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: -0.3,
                  margin: 0,
                  color: access ? R.brand : R.ink,
                  transition: "color 160ms",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("common.appName")}
              </h1>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 4,
                  fontSize: 12,
                  color: R.textSoft,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  // 1.2 вместо наследеното 1: с overflow:hidden кирилските
                  // descender-и (р/у/д) иначе се режат
                  lineHeight: 1.2,
                }}
              >
                <TagOutlined style={{ fontSize: 11, color: R.brandDeep }} />
                <span style={{ fontWeight: 600, color: R.ink }}>
                  {t("modules.ordering")}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={`flex shrink-0 items-center ${isNarrow ? "gap-1" : "gap-2"}`}>
          {/* ProfileDropdown сам решава: общ терминал със служител → име + директен
              бутон „Изход" (връщане към пин); личен акаунт → аватар с меню. */}
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </header>

      {logoutModal}
    </>
  );
}
